'use strict';

/**
 * Google Consent Mode v2 implementation for TCF compliance
 * This must be loaded before GTM
 */
(function() {
  // Initialize default consent state - deny all by default
  window.dataLayer = window.dataLayer || [];
  function gtag() { dataLayer.push(arguments); }

  // Set default consent state
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted' // Always granted for necessary cookies
  });

  /**
     * Parse TCF v2 consent string and extract purpose consents
     * @param {string} tc - The euconsent-v2 cookie value
     * @returns {Object} Purpose consents map
     */
  function parseTCFConsent(tc) {
    const consents = {
      1: true, // Strictly necessary - always true (will be overridden by actual parsing)
      2: false, // Functional
      3: false, // Analytics
      4: false, // Advertising
      5: false, // Content selection
      6: false, // Ad selection
      7: false, // Ad performance
      8: false, // Content performance
      9: false, // Market research
      10: false // Product development
    };

    if (!tc || !tc.startsWith('C')) {
      return consents;
    }

    try {
      // URL decode first (in case it's URL encoded)
      let decodedTC = tc;
      try {
        decodedTC = decodeURIComponent(tc);
      } catch (e) {
        // Not URL encoded, use original
      }

      // TCF strings can have multiple segments separated by '.'
      // We only need the first segment (Core String) for purposes
      const segments = decodedTC.split('.');
      const coreString = segments[0];

      // Convert Base64 URL safe to standard Base64
      let base64 = coreString.replace(/-/g, '+').replace(/_/g, '/');

      // Add padding if necessary
      while (base64.length % 4) {
        base64 += '=';
      }

      const decoded = atob(base64);

      // Convert to byte array
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }

      // In TCF v2, PurposeConsents start at bit offset 152:
      // Version (6) + Created (36) + LastUpdated (36) + CmpId (12) + CmpVersion (12)
      // + ConsentScreen (6) + ConsentLanguage (12) + VendorListVersion (12)
      // + TcfPolicyVersion (6) + IsServiceSpecific (1) + UseNonStandardStacks (1) + SpecialFeatureOptins (12) = 152 bits
      const startOffset = 152;

      // Extract consent for each purpose (1-10) - matching PHP logic
      for (let purposeId = 1; purposeId <= 10; purposeId++) {
        const bitIndex = startOffset + (purposeId - 1);
        const byteIndex = Math.floor(bitIndex / 8); // JavaScript uses 0-based arrays
        const bitInByte = bitIndex % 8;

        if (byteIndex < bytes.length) {
          // Perform big-endian bit mask: check if the specified bit is set
          const mask = 1 << (7 - bitInByte);
          const byteValue = bytes[byteIndex];
          const isSet = (byteValue & mask) !== 0;
          consents[purposeId] = isSet;
        }
      }

      // Force Purpose 1 to true after parsing (it should always be granted)
      consents[1] = true;
    } catch (e) {
      // Silent fallback - use default denied state
    }

    return consents;
  }

  /**
     * Parse USPrivacy string for consent purposes
     * @param {string} usprivacy - The usprivacy cookie value (format: "1YNN")
     * @returns {Object} Purpose consents map
     */
  function parseUSPrivacyConsent(usprivacy) {
    const consents = {
      1: true, // Strictly necessary - always true
      2: false, // Functional
      3: false, // Analytics
      4: false, // Advertising
      5: false, // Content selection
      6: false, // Ad selection
      7: false, // Ad performance
      8: false, // Content performance
      9: false, // Market research
      10: false // Product development
    };

    // Validate USPrivacy string format
    if (!usprivacy || usprivacy.length !== 4) {
      return consents;
    }

    // Check version (should be 1)
    if (usprivacy[0] !== '1') {
      return consents;
    }

    // Check if user opted out of sale (position 2)
    const optOutSale = usprivacy[2];

    // If user opted out of sale (Y), deny non-essential purposes
    if (optOutSale === 'Y') {
      return consents; // Only purpose 1 remains true
    }

    // For other purposes, allow if explicit notice given and no opt-out
    // US非CA地域において、CCPAダイアログがでても、 1N--(ユーザ回答によらず)がセットされるため、
    // フォールバックとして '1N' も通す。
    const explicitNotice = usprivacy[1];
    const allowNonEssential = explicitNotice === 'Y' || explicitNotice === 'N';

    if (allowNonEssential) {
      // Allow all purposes when no opt-out
      for (let i = 2; i <= 10; i++) {
        consents[i] = true;
      }
    }

    return consents;
  }

  /**
     * Update Google Consent Mode with TCF and GPP fallback
     */
  function updateConsentMode() {
    // First try TCF API
    if (typeof window.__tcfapi === 'function') {
      window.__tcfapi('getTCData', 2, function(tcData, success) {
        if (success && tcData && tcData.purpose && tcData.purpose.consents) {
          // Use TCF data regardless of gdprApplies status
          // This handles cases where GDPR dialog is shown outside GDPR regions
          updateConsentFromTCFData(tcData);
        } else {
          // Fallback to GPP if TCF not available or no purpose data
          updateConsentFromGPP();
        }
      });
    } else {
      // Fallback to GPP if TCF API not available
      updateConsentFromGPP();
    }
  }

  /**
     * Update consent from TCF data
     */
  function updateConsentFromTCFData(tcData) {
    const purposes = tcData.purpose.consents;

    // Map purposes to Google Consent types
    const consentUpdate = {
      // Analytics (Purpose 8: Content performance, Purpose 10: Product development)
      analytics_storage: (purposes[8] || purposes[10]) ? 'granted' : 'denied',

      // Advertising (Purpose 3: Create ad profile, Purpose 4: Select ads)
      ad_storage: (purposes[3] || purposes[4]) ? 'granted' : 'denied',
      ad_user_data: (purposes[3] || purposes[4]) ? 'granted' : 'denied',
      ad_personalization: purposes[3] ? 'granted' : 'denied',

      // Functional (Purpose 2: Technical storage)
      functionality_storage: purposes[2] ? 'granted' : 'denied',
      personalization_storage: purposes[2] ? 'granted' : 'denied',

      // Security is always granted
      security_storage: 'granted'
    };

    // Update consent state
    gtag('consent', 'update', consentUpdate);
  }

  /**
     * Update consent from GPP API (fallback)
     */
  function updateConsentFromGPP() {
    if (typeof window.__gpp === 'function') {
      window.__gpp('getUSPData', function(uspData, success) {
        if (success && uspData && uspData.uspString) {
          const consents = parseUSPrivacyConsent(uspData.uspString);
          updateConsentFromUSPrivacyData(consents);
        } else {
          // Final fallback: check USPrivacy cookie directly
          updateConsentFromUSPrivacyCookie();
        }
      });
    } else {
      // Final fallback: check USPrivacy cookie directly
      updateConsentFromUSPrivacyCookie();
    }
  }

  /**
     * Update consent from USPrivacy cookie (final fallback)
     */
  function updateConsentFromUSPrivacyCookie() {
    const usprivacyCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('usprivacy='));

    if (!usprivacyCookie) {
      return;
    }

    const usprivacy = usprivacyCookie.split('=')[1];
    const consents = parseUSPrivacyConsent(usprivacy);
    updateConsentFromUSPrivacyData(consents);
  }

  /**
     * Update consent from USPrivacy data
     */
  function updateConsentFromUSPrivacyData(consents) {
    // Map purposes to Google Consent types
    const consentUpdate = {
      // Analytics (Purpose 8: Content performance, Purpose 10: Product development)
      analytics_storage: (consents[8] || consents[10]) ? 'granted' : 'denied',

      // Advertising (Purpose 3: Create ad profile, Purpose 4: Select ads)
      ad_storage: (consents[3] || consents[4]) ? 'granted' : 'denied',
      ad_user_data: (consents[3] || consents[4]) ? 'granted' : 'denied',
      ad_personalization: consents[3] ? 'granted' : 'denied',

      // Functional (Purpose 2: Technical storage)
      functionality_storage: consents[2] ? 'granted' : 'denied',
      personalization_storage: consents[2] ? 'granted' : 'denied',

      // Security is always granted
      security_storage: 'granted'
    };

    // Update consent state
    gtag('consent', 'update', consentUpdate);
  }

  // Update consent mode on page load
  updateConsentMode();

  // Listen for TCF consent changes
  if (typeof window.__tcfapi === 'function') {
    window.__tcfapi('addEventListener', 2, function(tcData, success) {
      if (success && tcData && tcData.eventStatus === 'useractioncomplete' && tcData.purpose && tcData.purpose.consents) {
        // Use TCF data regardless of gdprApplies status for consent changes
        updateConsentFromTCFData(tcData);
      }
    });
  }

  // Listen for GPP consent changes
  if (typeof window.__gpp === 'function') {
    window.__gpp('addEventListener', function(evt) {
      if (evt && evt.eventName === 'sectionChange') {
        updateConsentFromGPP();
      }
    });
  }

  // Listen for consent changes (e.g., from CMP)
  window.addEventListener('message', function(event) {
    if (event.data && event.data.type === 'consent_updated') {
      updateConsentMode();
    }
  });
})();
